import type { Locale } from "./types";

/**
 * Translate Indonesian holiday names to English.
 * Strategy: pattern-based — match common stems (e.g. "Hari Raya Idul Fitri")
 * with optional suffix (Hijri year, etc.) preserved.
 */
const RULES: { test: RegExp; en: (m: RegExpMatchArray) => string }[] = [
  { test: /^Tahun Baru Masehi$/, en: () => "New Year's Day" },
  { test: /^Tahun Baru Imlek\s*(\d+)?$/, en: (m) => `Chinese New Year${m[1] ? ` ${m[1]}` : ""}` },
  { test: /^Isra Mi.?raj.*$/i, en: () => "Isra Mi'raj of the Prophet Muhammad" },
  { test: /^Hari Raya Nyepi.*$/, en: (m) => `Nyepi (Balinese New Year${/Saka\s+(\d+)/.exec(m[0])?.[1] ? ` Saka ${/Saka\s+(\d+)/.exec(m[0])![1]}` : ""})` },
  { test: /^Wafat Isa Almasih$/, en: () => "Good Friday" },
  { test: /^Hari Buruh Internasional$/, en: () => "International Labour Day" },
  { test: /^Hari Raya Waisak\s*(\d+)?$/, en: (m) => `Vesak Day${m[1] ? ` ${m[1]}` : ""}` },
  { test: /^Kenaikan Isa Almasih$/, en: () => "Ascension of Jesus Christ" },
  { test: /^Hari Raya Idul Fitri\s*(\d+\s*H)?$/, en: (m) => `Eid al-Fitr${m[1] ? ` ${m[1]}` : ""}` },
  { test: /^Hari Lahir Pancasila$/, en: () => "Pancasila Day" },
  { test: /^Hari Raya Idul Adha\s*(\d+\s*H)?$/, en: (m) => `Eid al-Adha${m[1] ? ` ${m[1]}` : ""}` },
  { test: /^Hari Kemerdekaan RI$/, en: () => "Indonesian Independence Day" },
  { test: /^Tahun Baru Islam\s*(\d+\s*H)?$/, en: (m) => `Islamic New Year${m[1] ? ` ${m[1]}` : ""}` },
  { test: /^Maulid Nabi Muhammad SAW$/, en: () => "Birthday of the Prophet Muhammad" },
  { test: /^Hari Raya Natal$/, en: () => "Christmas Day" },
  { test: /^Kebangkitan Yesus Kristus.*$/, en: () => "Easter Sunday" },
  { test: /^Hari Paskah$/, en: () => "Easter Sunday" },
  { test: /^Cuti Bersama Hari Suci Nyepi.*$/, en: () => "Joint Leave for Nyepi" },
  { test: /^Cuti Bersama Tahun Baru Imlek.*$/, en: () => "Joint Leave for Chinese New Year" },
  { test: /^Cuti Bersama Kenaikan.*$/, en: () => "Joint Leave for Ascension of Jesus Christ" },
  { test: /^Cuti Bersama Tahun Baru.*$/, en: () => "Joint Leave for New Year" },
  { test: /^Cuti Bersama Maulid.*$/, en: () => "Joint Leave for Mawlid" },
  { test: /^Cuti Bersama Idul Fitri.*$/, en: () => "Joint Leave for Eid al-Fitr" },
  { test: /^Cuti Bersama Idul Adha.*$/, en: () => "Joint Leave for Eid al-Adha" },
  { test: /^Cuti Bersama Imlek.*$/, en: () => "Joint Leave for Chinese New Year" },
  { test: /^Cuti Bersama Nyepi.*$/, en: () => "Joint Leave for Nyepi" },
  { test: /^Cuti Bersama Waisak.*$/, en: () => "Joint Leave for Vesak" },
  { test: /^Cuti Bersama Natal.*$/, en: () => "Joint Leave for Christmas" },
  { test: /^Cuti Bersama Akhir Tahun.*$/, en: () => "Joint Leave for Year-End" },
  { test: /^Cuti Bersama.*$/, en: () => "Joint Leave" },
];

export function translateHolidayName(name: string, locale: Locale): string {
  if (locale === "id") return name;
  for (const r of RULES) {
    const m = name.match(r.test);
    if (m) return r.en(m);
  }
  return name;
}

/**
 * Translate Hari Penting (commemorative day) names to English.
 * Universal/international days use standard English names.
 * Indonesia-specific national days get an English gloss.
 */
const HARI_PENTING_EN: Record<string, string> = {
  "Tahun Baru Masehi": "New Year's Day",
  "Hari Tritura": "Tritura Day",
  "Hari Gizi Nasional": "National Nutrition Day",
  "Hari Pers Nasional": "National Press Day",
  "Hari Valentine": "Valentine's Day",
  "Hari Pekerja Nasional": "National Workers' Day",
  "Hari Perempuan Internasional": "International Women's Day",
  "Hari Musik Nasional": "National Music Day",
  "Hari Supersemar": "Supersemar Day",
  "Hari Air Sedunia": "World Water Day",
  "Hari Kesehatan Sedunia": "World Health Day",
  "Hari Kartini": "Kartini Day",
  "Hari Bumi": "Earth Day",
  "Hari Buruh Internasional": "International Labour Day",
  "Hari Pendidikan Nasional": "National Education Day",
  "Hari Kebangkitan Nasional": "National Awakening Day",
  "Hari Lahir Pancasila": "Pancasila Day",
  "Hari Lingkungan Hidup Sedunia": "World Environment Day",
  "Hari Krida Pertanian": "Agricultural Day",
  "Hari Bhayangkara": "Police Day (Bhayangkara)",
  "Hari Kejaksaan": "Attorney's Day",
  "Hari Anak Nasional": "National Children's Day",
  "Hari Ulang Tahun ASEAN": "ASEAN Day",
  "Hari Pramuka": "Scout Day",
  "Hari Kemerdekaan RI": "Indonesian Independence Day",
  "Hari Kemanusiaan Sedunia": "World Humanitarian Day",
  "Hari Polwan": "Policewomen's Day",
  "Hari Aksara Internasional": "International Literacy Day",
  "Hari Perdamaian Dunia": "International Day of Peace",
  "Hari Tani Nasional": "National Farmers' Day",
  "Hari Kereta Api": "Railway Day",
  "Hari Kesaktian Pancasila": "Pancasila Sanctity Day",
  "Hari Batik Nasional": "National Batik Day",
  "Hari TNI": "Indonesian Armed Forces Day",
  "Hari Pangan Sedunia": "World Food Day",
  "Hari Sumpah Pemuda": "Youth Pledge Day",
  "Hari Pahlawan": "Heroes' Day",
  "Hari Kesehatan Nasional": "National Health Day",
  "Hari Guru Nasional": "National Teachers' Day",
  "Hari AIDS Sedunia": "World AIDS Day",
  "Hari Anti Korupsi Sedunia": "International Anti-Corruption Day",
  "Hari Hak Asasi Manusia": "Human Rights Day",
  "Hari Ibu": "Mother's Day",
  "Hari Natal": "Christmas Day",
};

export function translateHariPenting(name: string, locale: Locale): string {
  if (locale === "id") return name;
  return HARI_PENTING_EN[name] ?? name;
}

/** Translate category labels */
export function translateCategory(cat: string, locale: Locale): string {
  if (locale === "id") return cat;
  switch (cat) {
    case "keagamaan": return "religious";
    case "nasional": return "national";
    case "internasional": return "international";
    default: return cat;
  }
}